import React, { createContext, useContext, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);          // Firebase Auth user
  const [userProfile, setUserProfile] = useState(null); // Firestore profile
  const [loading, setLoading] = useState(true);

  /* ======================================
     🔥 Firebase Auth Listener (ENHANCED)
  ====================================== */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        await currentUser.reload();

        const updatedUser = { ...auth.currentUser };
        setUser(updatedUser);

        const uid = updatedUser.uid;
        const name = updatedUser.displayName || "User";
        const email = updatedUser.email || "";
        const mobile = updatedUser.phoneNumber || "";

        // 🔹 Keep existing localStorage (NO BREAK)
        localStorage.setItem("uid", uid);
        localStorage.setItem("name", name);
        localStorage.setItem("email", email);
        localStorage.setItem("mobile", mobile);

        // 🔥 NEW: FETCH FIRESTORE USER PROFILE
        try {
          const snap = await getDoc(doc(db, "users", uid));
          if (snap.exists()) {
            setUserProfile(snap.data());
          } else {
            setUserProfile(null);
          }
        } catch (err) {
          console.error("Failed to load user profile", err);
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);

        localStorage.removeItem("uid");
        localStorage.removeItem("name");
        localStorage.removeItem("email");
        localStorage.removeItem("mobile");
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /* ======================================
     🔥 Google Login
  ====================================== */
  const googleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Google login failed:", error);
      alert("Login failed. Please try again.");
    }
  };

  /* ======================================
     🔄 Refresh Profile (Auth only)
  ====================================== */
  const refreshUserProfile = async (updates = {}) => {
    try {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, updates);
        await auth.currentUser.reload();

        setUser({ ...auth.currentUser });

        const refreshed = auth.currentUser;
        localStorage.setItem("name", refreshed.displayName || "User");
        localStorage.setItem("email", refreshed.email || "");
      }
    } catch (error) {
      console.error("Profile update failed:", error);
    }
  };

  /* ======================================
     🚪 Logout
  ====================================== */
  const googleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);

      localStorage.removeItem("uid");
      localStorage.removeItem("name");
      localStorage.removeItem("email");
      localStorage.removeItem("mobile");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const value = {
    user,
    userProfile,   // ✅ NOW AVAILABLE EVERYWHERE
    loading,
    googleLogin,
    googleLogout,
    refreshUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
